import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Delete, Get, Put, Post } from '@/api/api-middleware';
import { apiPath } from '@/constants/defaultValues';

const initialState = {
  loading: false,
  selfServiceManualEntryList: [],
  AddUpdateDeleteResponse: [],
  ManualEntryResponse: [],
  WorkScheduleList: [],
  notification: { show: false, code: '' },
  WorkScheduleItem: {},
  error: '',
  employeeManualEntryDashboardResponse : {}
};

export const AddNewManualEntry = createAsyncThunk(
  'manualEntry/AddNewManualEntry',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/ManualEntry/Add'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const SelfService_fetchAllManualEntries = createAsyncThunk(
  'manualEntry/SelfService_fetchAllManualEntries ',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/ManualEntry/EmployeeGetAll'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchEmployeeManualEntryDashboard = createAsyncThunk(
  'manualEntry/fetchEmployeeManualEntryDashboard',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/ManualEntry/EmployeeManualEntryDashboard'}`,
        data: params,
      });
      return response?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchAllManualEntries = createAsyncThunk(
  'manualEntry/fetchAllManualEntries',
  async (params, thunkAPI) => {
    try {
      const response = await Post({
        url: `${apiPath}${'api/Operations/ManualEntry/GetAll'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const getWorkScheduleById = createAsyncThunk(
  'workSchedule/getWorkScheduleById',
  async (params, thunkAPI) => {
    try {
      const response = await Get({
        url: `${apiPath}api/Lookup/WorkSchedule/GetByID/${params}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateManualEntry = createAsyncThunk(
  'manualEntry/updateManualEntry',
  async (params, thunkAPI) => {
    try {
      const response = await Put({
        url: `${apiPath}${'api/Operations/ManualEntry/Update'}`,
        data: params,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteManualEntry = createAsyncThunk(
  'manualEntry/deleteManualEntry',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Operations/ManualEntry/Delete/${params.companyID}/${params.manualEntryID}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const selfService_deleteManualEntry = createAsyncThunk(
  'manualEntry/selfService_deleteManualEntry',
  async (params, thunkAPI) => {
    try {
      const response = await Delete({
        url: `${apiPath}api/Operations/ManualEntry/EmployeeDelete/${params.companyID}/${params.manualEntryID}`,
      });
      return response?.data?.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const manualEntrySlice = createSlice({
  name: 'manualEntry',
  initialState,
  reducers: {
    clearCurrentItem: (state) => {
      state.AddWorkScheduleResponse = [];
      state.PaginatedWorkScheduleResponse = [];
      state.WorkScheduleItem = {};
      state.employeeManualEntryDashboardResponse = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(AddNewManualEntry.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(AddNewManualEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(AddNewManualEntry.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(SelfService_fetchAllManualEntries.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.selfServiceManualEntryList = [];
      })
      .addCase(SelfService_fetchAllManualEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.selfServiceManualEntryList = action.payload;
        state.error = '';
      })
      .addCase(SelfService_fetchAllManualEntries.rejected, (state, action) => {
        state.loading = false;
        state.selfServiceManualEntryList = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchAllManualEntries.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.ManualEntryResponse = [];
      })
      .addCase(fetchAllManualEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.ManualEntryResponse = action.payload;
        state.error = '';
      })
      .addCase(fetchAllManualEntries.rejected, (state, action) => {
        state.loading = false;
        state.ManualEntryResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(getWorkScheduleById.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(getWorkScheduleById.fulfilled, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = action.payload;
        state.error = '';
      })
      .addCase(getWorkScheduleById.rejected, (state, action) => {
        state.loading = false;
        state.WorkScheduleItem = {};
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(updateManualEntry.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(updateManualEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(updateManualEntry.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(deleteManualEntry.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(deleteManualEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = action.payload;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(deleteManualEntry.rejected, (state, action) => {
        state.loading = false;
        state.AddUpdateDeleteResponse = [];
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(selfService_deleteManualEntry.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
      })
      .addCase(selfService_deleteManualEntry.fulfilled, (state, action) => {
        state.loading = false;
        state.notification = { show: true, code: '200' };
        state.error = '';
      })
      .addCase(selfService_deleteManualEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'An error occurred';
      });

    builder
      .addCase(fetchEmployeeManualEntryDashboard.pending, (state) => {
        state.loading = true;
        state.notification = { show: false, code: '' };
        state.employeeManualEntryDashboardResponse = {};
      })
      .addCase(fetchEmployeeManualEntryDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeManualEntryDashboardResponse = action.payload;

        state.error = '';
      })
      .addCase(fetchEmployeeManualEntryDashboard.rejected, (state, action) => {
        state.loading = false;
        state.employeeManualEntryDashboardResponse = action.payload;
        state.error = action.payload ?? 'An error occurred';
      });  
  },
});
export const { clearCurrentItem } = manualEntrySlice.actions;
export default manualEntrySlice.reducer;
